// components/use-case-details/use-case-workflow/use-case-workflow-process-overview-card.tsx
"use client";

import { motion } from 'framer-motion';
import { Activity, CheckCircle, UserCog, Users, XCircle } from 'lucide-react';
import React from 'react';

interface ProcessStep {
  description: string;
  actor: 'ai' | 'human' | 'system';
  hasDecision?: boolean;
}

interface ProcessOverviewCardProps {
  title?: string;
  steps: ProcessStep[];
}

const ProcessOverviewCard: React.FC<ProcessOverviewCardProps> = ({
  title = "Process Overview",
  steps
}) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  };

  // Get background color based on actor
  const getActorBg = (actor: string) => {
    switch(actor) {
      case 'ai': return 'bg-blue-600';
      case 'human': return 'bg-purple-600';
      case 'system': return 'bg-gray-700';
      default: return 'bg-blue-600';
    }
  };

  // Get actor icon
  const getActorIcon = (actor: string) => {
    switch(actor) {
      case 'ai': return <UserCog className="text-white" size={20} />;
      case 'human': return <Users className="text-white" size={20} />;
      case 'system': return <Activity className="text-white" size={20} />;
      default: return <UserCog className="text-white" size={20} />;
    }
  };
  
  // Get actor label
  const getActorLabel = (actor: string) => {
    switch(actor) {
      case 'ai': return 'AI';
      case 'human': return 'Human';
      case 'system': return 'System';
      default: return '';
    }
  };

  return (
    <div className="pt-4 pb-6">
      {title && <h3 className="text-xl font-bold text-white mb-6">{title}</h3>}

      <div className="relative">
        {/* Vertical line connecting all steps */}
        <div className="absolute left-6 top-0 bottom-0 w-px bg-gray-700" />
        
        {/* Steps */}
        <motion.div 
          className="space-y-8"
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {steps.map((step, index) => (
            <motion.div 
              key={index} 
              className="relative flex items-start"
              variants={itemVariants}
            >
              {/* Circle with icon */}
              <div className={`${getActorBg(step.actor)} w-12 h-12 rounded-full flex items-center justify-center z-10 mr-6`}>
                {getActorIcon(step.actor)}
              </div>
              
              <div className="flex-grow">
                {/* Step description with actor label on right */}
                <div className="flex justify-between items-start">
                  <p className="text-white">{step.description}</p>
                  <span className="text-gray-400 text-right ml-4">{getActorLabel(step.actor)}</span>
                </div>
                
                {/* Decision indicators */}
                {step.hasDecision && (
                  <div className="mt-2">
                    <span className="text-gray-400 mr-2">Decision:</span>
                    <CheckCircle className="inline-block text-green-500 mr-1" size={16} />
                    <XCircle className="inline-block text-red-500" size={16} />
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default ProcessOverviewCard;